import React from 'react';
import {
    Grid, Typography, Accordion, AccordionDetails,
    IconButton,
    AccordionSummary
} from '@mui/material';
import { AppToolbar } from '../../components/AppToolbar';
import { AppButton } from '../../components/AppButton';
import { Add, ArrowBack, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkoutsServices } from '../../services/workouts.service';
import { AppBox } from '../../components/AppBox';
import { AppAutocompleteField } from '../../components/AppAutocompleteField';
import { useForm, useWatch } from 'react-hook-form';
import { CRUDType } from '../../types/crud.type';
import { AppDateField } from '../../components/AppDateField';
import { EditFormRoutingParams } from '../common/EditFormComponent';
import { useSnackbar } from '../../hooks/snackbar.hook';
import { AppTextField } from '../../components/AppTextField';
import { WorkoutType } from '../../types/workout.type';
import { DateUtils } from '../../utils/date.utils';
import { PeriodizationType, PeriodizationWorkoutsType } from '../../types/periodizations.type';
import { PeriodizationsServices } from '../../services/periodizations.service';
import { HttpResponse } from '../../services/base.service';

export function PeriodizationEdit({ mode }: CRUDType) {
    const navigate = useNavigate();
    const { control, handleSubmit, setValue, getValues, reset, formState } = useForm<PeriodizationType>({});
    const formValues = useWatch({
        control,
        defaultValue: {},
    });
    const { id } = useParams<EditFormRoutingParams>();
    const { showSnackbar } = useSnackbar();

    const [workouts, setWorkouts] = React.useState<WorkoutType[]>();


    React.useEffect(() => {

        async function fetchData() {
            const workoutsResp = await WorkoutsServices.getAll()

            setWorkouts(workoutsResp.data);

            if (mode == 'edit') {
                PeriodizationsServices.getById(Number(id)).then((resp: HttpResponse<PeriodizationType>) => {
                    const classData = resp.data;
                    reset(classData);
                });
            }
            handleMinimumWorkouts()
        }

        fetchData();

    }, []);

    const handleMinimumWorkouts = (periodizationData?: PeriodizationType) => {
        const currentWorkouts = periodizationData?.periodization_workouts || getValues('periodization_workouts') || [];
        if (currentWorkouts.length < 1) {
            addWorkout();
        }
    };

    const addWorkout = () => {
        // Get the current worktous from the form state
        const currentState = getValues('periodization_workouts') || [];
        // Add a new empty schedule object to the array
        const newObj: PeriodizationWorkoutsType = {};

        // Update the workouts in the form state
        setValue('periodization_workouts', [...currentState, newObj]);
    };

    const removeWorkout = (index: number) => {
        const currentState = getValues('periodization_workouts') || [];
        if (currentState.length > 1) {
            currentState.splice(index, 1);
            setValue('periodization_workouts', currentState);
        } else {
            showSnackbar('At least one workout is required.', 'warning');
        }
    };

    const handleSave = async (data: PeriodizationType) => {
        try {
            if (mode == 'create') {
                await PeriodizationsServices.add(data)

            } else if (mode == 'edit' && id) {
                await PeriodizationsServices.update(id, data)
            }
            const action = mode === 'create' ? 'created' : 'updated';
            showSnackbar(`Class ${action} successfully!`, 'success');
            handleBack();
        } catch (error) {
            const msg = (error as any).response?.data?.message || 'An error occurred';
            console.error(msg, error);
            showSnackbar(msg, 'error');
        }
    };

    const handleBack = () => {
        navigate('/periodizations');
    }

    return (
        <form onSubmit={handleSubmit(handleSave)}>
            <AppToolbar title={`${mode === 'edit' ? `Edit` : `Add`} Periodization`}>
                <Grid container spacing={2} style={{ flex: 0.3 }}>
                    <Grid item>
                        <AppButton type='submit' disabled={!formState.isDirty}>
                            Save
                        </AppButton>
                    </Grid>
                    <Grid item>
                        <AppButton startIcon={<ArrowBack />} onClick={handleBack}>
                            Back
                        </AppButton>
                    </Grid>
                </Grid>
            </AppToolbar>
            <AppBox>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <AppTextField
                            name="name"
                            control={control}
                            label="Name"
                            rules={{ required: 'Name is required' }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <AppDateField
                            name="start_date"
                            control={control}
                            label="Start Date"
                            rules={{ required: 'Date is required' }}
                            transformValue={DateUtils.toISODateFormat}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <AppDateField
                            name="end_date"
                            control={control}
                            label="End Date"
                            rules={{ required: 'End Date is required' }}
                            transformValue={DateUtils.toISODateFormat}
                        />
                    </Grid>
                </Grid>
            </AppBox>
            {/* Schedule Section */}
            <AppBox>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Accordion expanded>
                            <AccordionSummary>
                                <Typography sx={{ flexGrow: 1, textAlign: 'center' }}>
                                    Workouts
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {formValues.periodization_workouts?.map((periodWorkout, index: number) => {
                                    const namePrefix = `periodization_workouts[${index}]`;
                                    return (<Grid container spacing={2} key={`${index} - ${periodWorkout.id ?? new Date().getMilliseconds()}`}>
                                        <Grid item xs={2}>
                                            <AppAutocompleteField
                                                control={control}
                                                multiple={false}
                                                name={`${namePrefix}.week_day`}
                                                label="Week Day"
                                                service={{
                                                    options: [
                                                        { id: 0, name: 'Sunday' },
                                                        { id: 1, name: 'Monday' },
                                                        { id: 2, name: 'Tuesday' },
                                                        { id: 3, name: 'Wednesday' },
                                                        { id: 4, name: 'Thursday' },
                                                        { id: 5, name: 'Friday' },
                                                        { id: 6, name: 'Saturday' }
                                                    ]
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <AppAutocompleteField
                                                control={control}
                                                rules={{ required: 'Field is required' }}
                                                multiple={false}
                                                name={`${namePrefix}.workout_id`}
                                                label={`Workout ${index + 1}`}
                                                service={{
                                                    options: workouts
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                                color='error'
                                                onClick={() => removeWorkout(index)} sx={{ ml: 'auto' }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Grid>
                                    </Grid>)
                                }

                                )}
                                <Grid item xs={12}>
                                    <AppButton startIcon={<Add />}
                                        onClick={addWorkout}
                                        variant="contained"
                                        sx={{ mt: 1, mb: 2 }}
                                    >
                                        Add Workout
                                    </AppButton>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </AppBox>
        </form >
    );
};
